import { useCallback } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { buildPaginatedEndpoint, convertDatesInObject } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import {
  pursuitBookmarkCheckQueryKeys,
  pursuitBookmarkIdsQueryKeys,
} from '@/hooks/pursuitBookmarks/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { PursuitBookmarkCheck } from '@/types/bookmark/PursuitBookmark'
import type { UUID } from '@/types/Id'
import type { Pursuit } from '@/types/pursuit/Pursuit'

interface UseBookmarkedPursuitIdsProps<T> {
  orgId: UUID | ''
  limit?: number
  offset?: number
  enabled?: boolean
  select?: (response: BookmarkedPursuitIdsResponse) => T
}

/**
 * Response type for bookmarked pursuit IDs.
 * Contains just the IDs and count for lightweight queries and optimistic updates.
 */
export interface BookmarkedPursuitIdsResponse {
  pursuitIds: UUID[]
  count: number
}

/**
 * Fetches bookmarked pursuits and extracts just the IDs.
 *
 * Note: The backend returns full Pursuit objects. Ideally we'd have an endpoint
 * that returns only IDs to reduce data transfer, but extracting IDs client-side
 * is acceptable since users typically have few bookmarks (<100).
 */
const fetchBookmarkedPursuitIds = async (
  orgId: UUID | '',
  accessToken: string | undefined | null,
  limit?: number,
  offset?: number,
): Promise<BookmarkedPursuitIdsResponse> => {
  if (accessToken === null || accessToken === undefined || !orgId) {
    return { pursuitIds: [], count: 0 }
  }

  // Fetch all bookmarks (users typically have few)
  const url = buildPaginatedEndpoint(
    `${API_URL}/api/v1/organizations/${orgId}/pursuits/bookmarks`,
    { limit: limit ?? 10000, offset: offset ?? 0 },
  )

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }

  const response = convertDatesInObject(
    await res.json(),
  ) as PaginatedResponse<Pursuit>

  return {
    pursuitIds: response.data.map((p) => p.id),
    count: response.count,
  }
}

/**
 * Hook that fetches only the IDs of bookmarked pursuits.
 *
 * This is the lightweight query used for optimistic updates.
 * When a bookmark is created/deleted, we optimistically update just this ID list,
 * which is much simpler than trying to construct a full Pursuit object.
 *
 * Use with `usePursuitsByIds` to get the full pursuit data.
 */
export const useBookmarkedPursuitIds = <T = BookmarkedPursuitIdsResponse>({
  orgId,
  enabled = true,
  select,
  limit,
  offset,
}: UseBookmarkedPursuitIdsProps<T>) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const selectFn = useCallback(
    (response: BookmarkedPursuitIdsResponse): T => {
      if (select) {
        return select(response)
      }
      return response as unknown as T
    },
    [select],
  )

  return useQuery({
    queryKey: pursuitBookmarkIdsQueryKeys.organization({
      orgId,
      limit,
      offset,
    }),
    queryFn: async () => {
      const response = await fetchBookmarkedPursuitIds(
        orgId,
        accessToken,
        limit,
        offset,
      )

      // Prefill bookmark check cache for each pursuit ID
      for (const pursuitId of response.pursuitIds) {
        const checkKey = pursuitBookmarkCheckQueryKeys.check({
          orgId,
          pursuitId,
        })
        const checkData: PursuitBookmarkCheck = {
          is_bookmarked: true,
          pursuit_id: pursuitId,
        }
        queryClient.setQueryData(checkKey, checkData)
      }

      return response
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: enabled && !!orgId,
    select: selectFn,
  })
}

interface UsePursuitIsBookmarkedProps {
  orgId: UUID | ''
  pursuitId: UUID | ''
  enabled?: boolean
}

export const usePursuitIsBookmarked = ({
  orgId,
  pursuitId,
  enabled = true,
}: UsePursuitIsBookmarkedProps) => {
  const selectFn = useCallback(
    (response: BookmarkedPursuitIdsResponse) => {
      if (!pursuitId) {
        return false
      }
      return response.pursuitIds.includes(pursuitId)
    },
    [pursuitId],
  )

  return useBookmarkedPursuitIds({
    orgId,
    enabled,
    select: selectFn,
  })
}
